import { EXECUTOR_LABELS, LIMIT_HELP, PRESETS, ruleParts, ruleSummary } from '@/lib/scaling';
import { locationOptions } from '@/lib/locations';
import { cloudProvider, PROVIDER_TEXTS } from '@/lib/providers';
import type { Product } from '@/api/products';
import type { SshKeyDto } from '@/api/hetzner';
import type {
  McpHandoffDraft,
  McpHandoffGithubScope,
  McpHandoffSubmission,
} from '@/api/mcp-handoffs';

export interface SecretField {
  /** `runnerToken`, `githubToken`, `hetznerAuthToken`, `providerAuthToken`, `secretKey` or `header:<name>`. */
  key: string;
  label: string;
  hint?: string;
}

const HEADER = 'header:';

export function handoffSecretFields(draft: McpHandoffDraft): SecretField[] {
  return draft.secrets.map((key) => {
    switch (key) {
      case 'runnerToken':
        return {
          key,
          label: 'GitLab runner authentication token',
          hint:
            draft.kind === 'scaling_policy'
              ? `The token ${draft.name} is registered with; burst runners share it. It starts with glrt-.`
              : 'Create a runner in GitLab under Settings → CI/CD → Runners; the token starts with glrt-.',
        };
      case 'githubToken':
        return {
          key,
          label: GITHUB_TOKEN_LABEL,
          hint: draft.github ? githubTokenHint(draft.github) : '',
        };
      case 'hetznerAuthToken':
        return {
          key,
          label: 'Hetzner Cloud API token',
          hint: 'A read & write token of the Hetzner project the server should run in.',
        };
      case 'providerAuthToken': {
        const texts = PROVIDER_TEXTS[cloudProvider(draft.provider)];
        return { key, label: `${texts.name} API token`, hint: texts.tokenHelp };
      }
      case 'secretKey':
        return {
          key,
          label: 'S3 secret key',
          hint:
            draft.action === 'update'
              ? 'Replaces the stored secret key.'
              : `Belongs to the access key ID ${draft.accessKeyId ?? ''}.`.trim(),
        };
      default:
        return { key, label: `Value of the ${key.slice(HEADER.length)} header` };
    }
  });
}

export const GITHUB_TOKEN_LABEL = 'GitHub fine-grained personal access token';

/** The permission a GitHub runner's token needs, by its scope. */
export function githubTokenPermission(organization: boolean): string {
  return organization
    ? 'the organization permission Self-hosted runners: Read and write'
    : 'the repository permission Administration: Read and write';
}

export function githubTokenHint(
  github: Pick<McpHandoffGithubScope, 'owner' | 'repository'>
): string {
  return github.repository
    ? `Its resource owner must be ${github.owner}, with access to the repository ${github.repository} and ` +
        `${githubTokenPermission(false)}.`
    : `Its resource owner must be the organization ${github.owner}, with ${githubTokenPermission(true)}.`;
}

function yesNo(value: boolean | null | undefined): string {
  return value ? 'Yes' : 'No';
}

function watchdogRow(draft: McpHandoffDraft): string | undefined {
  if (draft.watchdogEnabled == null) return undefined;
  if (!draft.watchdogEnabled) return 'No';
  return draft.watchdogTimeoutMinutes != null
    ? `Yes, restarts after ${draft.watchdogTimeoutMinutes} min without a report`
    : 'Yes';
}

export interface HandoffRow {
  label: string;
  value: string;
  /** Shown one per line instead of the value; a line wraps only between its parts. */
  lines?: string[][];
}

/** What the page has loaded to show a scaling policy's burst template by name; missing parts show the raw values. */
export interface HandoffLookups {
  products?: Product[];
  /** The keys listed with the entered token of the draft's provider. */
  sshKeys?: SshKeyDto[];
}

function burstTemplateRows(
  draft: McpHandoffDraft,
  { products = [], sshKeys = [] }: HandoffLookups
): [string, string | null | undefined][] {
  const product = products.find((p) => p.productId === draft.productId);
  const location = locationOptions(product).find((loc) => loc.code === draft.location);
  const keyNames = new Map(sshKeys.map((key) => [String(key.id), key.name]));
  return [
    [
      'Burst server type',
      product
        ? `${product.name} · ${product.cpus} vCPU · ${product.memory} GB RAM`
        : draft.productId,
    ],
    ['Location', location ? `${location.flag} ${location.name}` : draft.location],
    ...githubRows(draft),
    ['GitLab', draft.gitlabHost],
    ['Executor', (draft.executor && EXECUTOR_LABELS[draft.executor]) ?? draft.executor],
    ['Concurrency', draft.concurrency?.toString()],
    ['Labels', draft.labels?.join(', ')],
    ['SSH keys', draft.sshKeyIds?.map((id) => keyNames.get(id) ?? id).join(', ')],
  ];
}

/** The secret holding the API token of the draft's cloud provider, if the page asks for one. */
export function cloudTokenSecret(draft: McpHandoffDraft): string | undefined {
  return draft.secrets.find((key) => key === 'hetznerAuthToken' || key === 'providerAuthToken');
}

/** The read-only settings shown for confirmation; empty optional settings are left out. */
export function handoffRows(draft: McpHandoffDraft, lookups: HandoffLookups = {}): HandoffRow[] {
  const rows: [string, string | null | undefined][] = [['Name', draft.name]];
  // Hetzner drafts have no provider and show none, as before other providers existed.
  if (draft.provider)
    rows.push(['Cloud provider', PROVIDER_TEXTS[cloudProvider(draft.provider)].name]);
  if (draft.kind === 'runner') {
    rows.push(
      [
        'Size',
        [
          draft.productId,
          draft.cpus != null ? `${draft.cpus} vCPU` : null,
          draft.memory != null ? `${draft.memory} GB RAM` : null,
          draft.disk != null ? `${draft.disk} GB disk` : null,
        ]
          .filter(Boolean)
          .join(', '),
      ],
      ['Location', [draft.city, draft.country].filter(Boolean).join(', ') || draft.location],
      ...githubRows(draft),
      ['Executor', draft.executor],
      ['Concurrency', draft.concurrency?.toString()],
      ['GitLab', draft.gitlabHost],
      ['Labels', draft.labels?.join(', ')],
      ['SSH keys', draft.sshKeyIds?.join(', ')],
      ['Monitoring', yesNo(draft.monitoringEnabled)],
      ['Metrics connection', draft.metricsConnectionName],
      ['Watchdog', watchdogRow(draft)],
      ['Cache', draft.cacheName]
    );
  } else if (draft.kind === 'scaling_policy') {
    rows.push(
      ['Enabled', yesNo(draft.policyEnabled)],
      [
        'When to scale',
        draft.preset
          ? PRESETS[draft.preset].label
          : (draft.rules ?? []).map(ruleSummary).join('; '),
      ],
      [LIMIT_HELP.maxBurstRunners.name, draft.maxBurstRunners?.toString()],
      [LIMIT_HELP.stepSize.name, draft.stepSize?.toString()],
      [
        LIMIT_HELP.scaleInWindowMinutes.name,
        draft.scaleInWindowMinutes != null ? `${draft.scaleInWindowMinutes} min` : null,
      ],
      [
        LIMIT_HELP.stabilizationMinutes.name,
        draft.stabilizationMinutes != null ? `${draft.stabilizationMinutes} min` : null,
      ],
      ...burstTemplateRows(draft, lookups),
      ['Metrics connection', draft.metricsConnectionName],
      ['Cache', draft.cacheName]
    );
  } else if (draft.kind === 'cache') {
    rows.push(
      ['Server', `${draft.insecure ? 'http' : 'https'}://${draft.serverAddress ?? ''}`],
      ['Bucket', draft.bucketName],
      ['Region', draft.bucketLocation],
      ['Path prefix', draft.pathPrefix],
      ['Shared', yesNo(draft.shared)],
      [
        'Path style',
        draft.pathStyle == null ? 'Detect' : draft.pathStyle ? 'Path-style' : 'Virtual-hosted',
      ],
      ['Access key ID', draft.accessKeyId]
    );
  } else {
    rows.push(
      ['Endpoint', draft.endpoint],
      [
        'Headers',
        draft.headers
          ?.map((header) => `${header.name} (${header.newValue ? 'new value' : 'keeps its value'})`)
          .join(', '),
      ]
    );
  }
  return rows
    .filter((row): row is [string, string] => typeof row[1] === 'string' && row[1] !== '')
    .map(([label, value]) =>
      label === 'When to scale' && draft.kind === 'scaling_policy' && !draft.preset
        ? { label, value, lines: (draft.rules ?? []).map(ruleParts) }
        : { label, value }
    );
}

function githubRows(draft: McpHandoffDraft): [string, string | null | undefined][] {
  return draft.github ? githubScopeRows(draft.github) : [];
}

export function githubScopeRows(github: McpHandoffGithubScope): [string, string][] {
  return github.repository
    ? [['GitHub repository', `${github.owner}/${github.repository}`]]
    : [
        ['GitHub organization', github.owner],
        ['Runner group', github.runnerGroup ?? 'Default'],
      ];
}

export function handoffSubmission(
  draft: McpHandoffDraft,
  values: Record<string, string>,
  recreateRunners: boolean
): McpHandoffSubmission {
  const value = (key: string) => (values[key] ?? '').trim();
  const submission: McpHandoffSubmission = { stateToken: draft.stateToken, recreateRunners };
  for (const key of draft.secrets) {
    if (key.startsWith(HEADER)) {
      submission.headers = [
        ...(submission.headers ?? []),
        { name: key.slice(HEADER.length), value: value(key) },
      ];
    } else if (
      key === 'runnerToken' ||
      key === 'githubToken' ||
      key === 'hetznerAuthToken' ||
      key === 'providerAuthToken' ||
      key === 'secretKey'
    ) {
      submission[key] = value(key);
    }
  }
  return submission;
}
